import { useEffect, useRef, useState } from "react";
import { CreditPlan } from "../types";


interface PlanSliderProps {
  plans: CreditPlan[];
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  selectedPlan: CreditPlan;
}

const PlanSlider = ({ plans, selectedIndex, setSelectedIndex, selectedPlan }: PlanSliderProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && sliderRef.current && plans.length > 1) {
        const rect = sliderRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const width = rect.width;
        const percentage = Math.max(0, Math.min(1, x / width));
        const newIndex = Math.round(percentage * (plans.length - 1));
        setSelectedIndex(newIndex);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, plans.length, setSelectedIndex]);

  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging && sliderRef.current && plans.length > 1) {
        const rect = sliderRef.current.getBoundingClientRect();
        const x = e.touches[0].clientX - rect.left;
        const width = rect.width;
        const percentage = Math.max(0, Math.min(1, x / width));
        const newIndex = Math.round(percentage * (plans.length - 1));
        setSelectedIndex(newIndex);
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, plans.length, setSelectedIndex]);

  const getSliderPosition = () => {
    if (plans.length <= 1) return "0%";
    return `${(selectedIndex / (plans.length - 1)) * 100}%`;
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  return (
    <>
      <p className="text-sm text-grease-100">How many Tokens would you like to purchase</p>
      <div className="mt-6 relative" ref={sliderRef}>
        <div
          className="h-1 w-full bg-indigo-50 rounded-full cursor-pointer"
          onMouseDown={handleMouseDown}
          onTouchStart={() => setIsDragging(true)}
        >
          <input
            type="range"
            min={0}
            max={plans.length - 1}
            value={selectedIndex}
            onChange={(e) => setSelectedIndex(Number(e.target.value))}
            className="absolute top-0 left-0 w-full h-2 opacity-0 cursor-pointer z-10"
          />
          <div
            className="absolute top-0 left-0 h-1.5 rounded-full"
            style={{
              width: getSliderPosition(),
              background: "#FFD700",
            }}
          ></div>
          <div
            className={`absolute top-0 h-4 w-4 rounded-full border-2 border-white shadow-md transition-transform duration-75 ease-out ${isDragging ? 'scale-110' : ''}`}
            style={{
              left: getSliderPosition(),
              transform: 'translateX(-50%) translateY(-25%)',
              background: "#FFD700",
              boxShadow: "0 1px 3px rgba(0,0,0,0.2), 0 0 0 1px rgba(68, 102, 242, 0.2)",
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={() => setIsDragging(true)}
          ></div>
        </div>
        <div className="absolute top-0 left-0 w-full h-1">
          {plans.map((plan, index) => {
            const isActive = index <= selectedIndex;
            const isSelected = index === selectedIndex;
            // Same left-percentage formula as the thumb (getSliderPosition), so
            // each marker's dot and label sit exactly under the thumb when selected
            // instead of relying on flex justify-between spacing (which drifts from
            // the percentage-based thumb position for uneven label widths).
            const left = plans.length <= 1 ? "0%" : `${(index / (plans.length - 1)) * 100}%`;
            const isFirst = index === 0;
            const isLast = index === plans.length - 1;
            // The dot always centers on its exact track position. The label centers
            // too, except at the two ends — there it hugs the track's edge instead,
            // so wide labels like "01"/"100" don't get clipped off the container.
            const labelTranslate = isFirst ? "0%" : isLast ? "-100%" : "-50%";
            return (
              <div
                key={index}
                className="absolute top-0 cursor-pointer"
                style={{ left }}
                onClick={() => setSelectedIndex(index)}
              >
                <div
                  className={`w-1 h-1 rounded-full ${isActive ? 'bg-[#FFD700]' : 'bg-indigo-300'}`}
                  style={{ transform: "translateX(-50%)" }}
                ></div>
                <p
                  className={`absolute top-4 text-sm whitespace-nowrap ${isSelected ? 'text-[#FFD700] font-medium' : 'text-[#5A6573]'}`}
                  style={{ transform: `translateX(${labelTranslate})` }}
                >
                  {plan.creditNumber?.toString().padStart(2, '0') || "00"}
                </p>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-sm text-lightgray-200 mt-14 pb-4 border-b border-lightgray-100 border-dashed">
        $ {selectedPlan.creditNumber > 0 ? (selectedPlan.amount / selectedPlan.creditNumber).toFixed(2) : "0.00"} Per Token
      </p>
    </>
  );
};

export default PlanSlider;